import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Save, X, Percent, DollarSign, Edit2 } from 'lucide-react';
import { invoiceService, userService } from '../../services/api';
import ConfirmDialog from '../common/ConfirmDialog';

const ITEM_TYPES = [
  { value: 'time', label: 'Time' },
  { value: 'expense', label: 'Expense' },
  { value: 'flat_fee', label: 'Flat Fee' },
  { value: 'discount', label: 'Discount' },
  { value: 'late_fee', label: 'Late Fee' },
];

const emptyLineItem = {
  description: '',
  date: new Date().toISOString().split('T')[0],
  item_type: 'time',
  attorney_id: '',
  hours: '',
  rate: '',
  quantity: 1,
  unit_price: '',
  amount: '',
  billable: true,
  discount_type: null,
  discount_value: 0,
};

function computeAmount(item) {
  let base = 0;
  if (item.item_type === 'time') {
    base = (parseFloat(item.hours) || 0) * (parseFloat(item.rate) || 0);
  } else {
    base = (parseFloat(item.quantity) || 1) * (parseFloat(item.unit_price) || 0);
  }
  if (item.discount_type === 'percentage') {
    base -= base * ((parseFloat(item.discount_value) || 0) / 100);
  } else if (item.discount_type === 'fixed') {
    base -= parseFloat(item.discount_value) || 0;
  }
  return Math.round(base * 100) / 100;
}

const EditableLineItems = ({ invoice, onInvoiceUpdated, showNotification }) => {
  const isDraft = invoice.status === 'draft';
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [addingNew, setAddingNew] = useState(false);
  const [newItem, setNewItem] = useState({ ...emptyLineItem });
  const [saving, setSaving] = useState(false);
  const [attorneys, setAttorneys] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: null, variant: 'warning', confirmText: 'Confirm' });

  useEffect(() => {
    if (isDraft) {
      userService.getUsers().then(res => {
        const active = (res.data || []).filter(u => u.firm_active !== false && !u.has_pending_invitation);
        setAttorneys(active);
      }).catch(() => {});
    }
  }, [isDraft]);

  // Invoice-level discount state
  const [editingDiscount, setEditingDiscount] = useState(false);
  const [invoiceDiscountType, setInvoiceDiscountType] = useState(invoice.discount_type || null);
  const [invoiceDiscountValue, setInvoiceDiscountValue] = useState(invoice.discount_value || 0);

  const startEditing = (item) => {
    setEditingId(item.id);
    setEditData({
      description: item.description,
      attorney_id: item.attorney_id || '',
      _attorney_name: item.attorney_name || '',
      date: item.date,
      item_type: item.item_type,
      hours: item.hours || '',
      rate: item.rate || '',
      quantity: item.quantity || 1,
      unit_price: item.unit_price || '',
      amount: item.amount,
      billable: item.billable !== false,
      discount_type: item.discount_type || null,
      discount_value: item.discount_value || 0,
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditData({});
  };

  const autoFillRate = (updated, field, value) => {
    if (field === 'attorney_id' && value && updated.item_type === 'time') {
      const attorney = attorneys.find(a => a.id === value);
      if (attorney?.hourly_rate) {
        updated.rate = parseFloat(attorney.hourly_rate).toFixed(2);
      }
    }
  };

  const handleEditChange = (field, value) => {
    const updated = { ...editData, [field]: value };
    autoFillRate(updated, field, value);
    if (['hours', 'rate', 'quantity', 'unit_price', 'item_type', 'discount_type', 'discount_value'].includes(field) ||
        (field === 'attorney_id' && updated.item_type === 'time')) {
      updated.amount = computeAmount(updated);
    }
    setEditData(updated);
  };

  const handleNewChange = (field, value) => {
    const updated = { ...newItem, [field]: value };
    autoFillRate(updated, field, value);
    if (['hours', 'rate', 'quantity', 'unit_price', 'item_type', 'discount_type', 'discount_value'].includes(field) ||
        (field === 'attorney_id' && updated.item_type === 'time')) {
      updated.amount = computeAmount(updated);
    }
    setNewItem(updated);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await invoiceService.updateLineItem(invoice.id, editingId, {
        description: editData.description,
        attorney_id: editData.attorney_id || null,
        date: editData.date,
        item_type: editData.item_type,
        hours: editData.item_type === 'time' ? parseFloat(editData.hours) || null : null,
        rate: editData.item_type === 'time' ? parseFloat(editData.rate) || null : null,
        quantity: editData.item_type !== 'time' ? parseFloat(editData.quantity) || 1 : null,
        unit_price: editData.item_type !== 'time' ? parseFloat(editData.unit_price) || null : null,
        amount: parseFloat(editData.amount) || 0,
        billable: editData.billable,
        discount_type: editData.discount_type || null,
        discount_value: parseFloat(editData.discount_value) || 0,
      });
      setEditingId(null);
      setEditData({});
      onInvoiceUpdated();
      showNotification('success', 'Line item updated');
    } catch (error) {
      showNotification('error', 'Failed to update line item');
    } finally {
      setSaving(false);
    }
  };

  const saveNewItem = async () => {
    if (!newItem.description || !newItem.date) {
      showNotification('error', 'Description and date are required');
      return;
    }
    setSaving(true);
    try {
      await invoiceService.addLineItem(invoice.id, {
        description: newItem.description,
        attorney_id: newItem.attorney_id || null,
        date: newItem.date,
        item_type: newItem.item_type,
        hours: newItem.item_type === 'time' ? parseFloat(newItem.hours) || null : null,
        rate: newItem.item_type === 'time' ? parseFloat(newItem.rate) || null : null,
        quantity: newItem.item_type !== 'time' ? parseFloat(newItem.quantity) || 1 : null,
        unit_price: newItem.item_type !== 'time' ? parseFloat(newItem.unit_price) || null : null,
        amount: parseFloat(newItem.amount) || 0,
        billable: newItem.billable,
        discount_type: newItem.discount_type || null,
        discount_value: parseFloat(newItem.discount_value) || 0,
      });
      setAddingNew(false);
      setNewItem({ ...emptyLineItem });
      onInvoiceUpdated();
      showNotification('success', 'Line item added');
    } catch (error) {
      showNotification('error', 'Failed to add line item');
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = (lineItemId) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Line Item',
      message: 'Delete this line item?',
      variant: 'danger',
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          await invoiceService.deleteLineItem(invoice.id, lineItemId);
          onInvoiceUpdated();
          showNotification('success', 'Line item deleted');
        } catch (error) {
          showNotification('error', 'Failed to delete line item');
        }
      },
    });
  };

  const saveInvoiceDiscount = async () => {
    setSaving(true);
    try {
      await invoiceService.updateInvoice(invoice.id, {
        discount_type: invoiceDiscountType || null,
        discount_value: parseFloat(invoiceDiscountValue) || 0,
      });
      setEditingDiscount(false);
      onInvoiceUpdated();
      showNotification('success', 'Invoice discount updated');
    } catch (error) {
      showNotification('error', 'Failed to update discount');
    } finally {
      setSaving(false);
    }
  };

  const renderDiscountCell = (item, data, onChange) => {
    const dt = data.discount_type;
    return (
      <div className="flex items-center space-x-1">
        <select
          value={dt || ''}
          onChange={(e) => {
            const val = e.target.value || null;
            onChange('discount_type', val);
          }}
          className="w-16 border border-gray-300 rounded text-xs px-1 py-1"
        >
          <option value="">None</option>
          <option value="fixed">$</option>
          <option value="percentage">%</option>
        </select>
        {dt && (
          <input
            type="number"
            step="0.01"
            min="0"
            value={data.discount_value || ''}
            onChange={(e) => onChange('discount_value', e.target.value)}
            className="w-16 border border-gray-300 rounded text-xs px-1 py-1"
            placeholder={dt === 'percentage' ? '%' : '$'}
          />
        )}
      </div>
    );
  };

  const renderEditRow = (data, onChange, onSave, onCancel) => (
    <>
      <td className="px-4 py-2">
        <input
          type="text"
          value={data.description}
          onChange={(e) => onChange('description', e.target.value)}
          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
        />
      </td>
      <td className="px-4 py-2">
        <select
          value={data.attorney_id || ''}
          onChange={(e) => onChange('attorney_id', e.target.value || null)}
          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
        >
          <option value="">Select...</option>
          {attorneys.map(attorney => (
            <option key={attorney.id} value={attorney.id}>
              {attorney.first_name} {attorney.last_name}
            </option>
          ))}
        </select>
      </td>
      <td className="px-4 py-2">
        <input
          type="date"
          value={data.date}
          onChange={(e) => onChange('date', e.target.value)}
          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
        />
      </td>
      <td className="px-4 py-2">
        <select
          value={data.item_type}
          onChange={(e) => onChange('item_type', e.target.value)}
          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
        >
          {ITEM_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </td>
      <td className="px-4 py-2">
        {data.item_type === 'time' ? (
          <input
            type="number"
            step="0.25"
            min="0"
            value={data.hours}
            onChange={(e) => onChange('hours', e.target.value)}
            className="w-20 border border-gray-300 rounded px-2 py-1 text-sm"
          />
        ) : (
          <input
            type="number"
            step="1"
            min="0"
            value={data.quantity}
            onChange={(e) => onChange('quantity', e.target.value)}
            className="w-20 border border-gray-300 rounded px-2 py-1 text-sm"
            placeholder="Qty"
          />
        )}
      </td>
      <td className="px-4 py-2">
        {data.item_type === 'time' ? (
          <input
            type="number"
            step="0.01"
            min="0"
            value={data.rate}
            onChange={(e) => onChange('rate', e.target.value)}
            className="w-24 border border-gray-300 rounded px-2 py-1 text-sm"
            placeholder="$/hr"
          />
        ) : (
          <input
            type="number"
            step="0.01"
            min="0"
            value={data.unit_price}
            onChange={(e) => onChange('unit_price', e.target.value)}
            className="w-24 border border-gray-300 rounded px-2 py-1 text-sm"
            placeholder="Unit $"
          />
        )}
      </td>
      <td className="px-4 py-2">
        {renderDiscountCell(null, data, onChange)}
      </td>
      <td className="px-4 py-2 text-center">
        <input
          type="checkbox"
          checked={data.billable}
          onChange={(e) => onChange('billable', e.target.checked)}
          className="h-4 w-4 text-yellow-600 rounded border-gray-300"
        />
      </td>
      <td className="px-4 py-2 text-right">
        <input
          type="number"
          step="0.01"
          value={data.amount}
          onChange={(e) => onChange('amount', e.target.value)}
          className="w-24 border border-gray-300 rounded px-2 py-1 text-sm text-right"
        />
      </td>
      <td className="px-4 py-2 text-right">
        <div className="flex items-center justify-end space-x-1">
          <button
            onClick={onSave}
            disabled={saving}
            className="text-green-600 hover:text-green-800 p-1"
            title="Save"
          >
            <Save className="w-4 h-4" />
          </button>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 p-1"
            title="Cancel"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </td>
    </>
  );

  const renderReadRow = (item) => (
    <>
      <td className="px-4 py-3">
        <div>
          <p className="text-sm font-medium text-gray-900">{item.description}</p>
          {item.task_title && (
            <p className="text-xs text-gray-500">Task: {item.task_title}</p>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-gray-900">
        {item.attorney_name || '-'}
      </td>
      <td className="px-4 py-3 text-sm text-gray-900">
        {invoiceService.formatDate(item.date)}
      </td>
      <td className="px-4 py-3">
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          {invoiceService.getLineItemTypeIcon(item.item_type)} {item.item_type}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-gray-900">
        {item.item_type === 'time'
          ? (item.hours || '-')
          : (item.quantity && parseFloat(item.quantity) !== 1 ? item.quantity : '-')}
      </td>
      <td className="px-4 py-3 text-sm text-gray-900">
        {item.item_type === 'time'
          ? (item.rate ? invoiceService.formatCurrency(item.rate) : '-')
          : (item.unit_price ? invoiceService.formatCurrency(item.unit_price) : '-')}
      </td>
      <td className="px-4 py-3 text-sm text-gray-900">
        {item.discount_type ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-orange-50 text-orange-700">
            {item.discount_type === 'percentage' ? (
              <><Percent className="w-3 h-3 mr-1" />{item.discount_value}%</>
            ) : (
              <><DollarSign className="w-3 h-3 mr-1" />{item.discount_value}</>
            )}
          </span>
        ) : '-'}
      </td>
      <td className="px-4 py-3 text-sm text-center">
        {item.billable === false ? (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
            No
          </span>
        ) : (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Yes
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">
        {invoiceService.formatCurrency(item.amount)}
      </td>
      <td className="px-4 py-3 text-right">
        {isDraft && (
          <div className="flex items-center justify-end space-x-1">
            <button
              onClick={() => startEditing(item)}
              className="text-yellow-600 hover:text-yellow-800 p-1"
              title="Edit"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            <button
              onClick={() => deleteItem(item.id)}
              className="text-red-400 hover:text-red-600 p-1"
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </td>
    </>
  );

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Line Items</h3>
        {isDraft && !addingNew && (
          <button
            onClick={() => setAddingNew(true)}
            className="bg-yellow-500 text-white px-3 py-1.5 rounded-lg hover:bg-yellow-600 flex items-center text-sm"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Line Item
          </button>
        )}
      </div>

      {invoice.line_items && invoice.line_items.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Performed By</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hours/Qty</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rate/Price</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Discount</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Billable</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase w-20"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {invoice.line_items.map((item) => (
                <tr key={item.id} className={editingId === item.id ? 'bg-yellow-50' : 'hover:bg-gray-50'}>
                  {editingId === item.id
                    ? renderEditRow(editData, handleEditChange, saveEdit, cancelEditing)
                    : renderReadRow(item)}
                </tr>
              ))}
              {addingNew && (
                <tr className="bg-green-50">
                  {renderEditRow(newItem, handleNewChange, saveNewItem, () => {
                    setAddingNew(false);
                    setNewItem({ ...emptyLineItem });
                  })}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="px-6 py-8 text-center">
          <p className="text-gray-600 mb-4">No line items yet</p>
          {isDraft && !addingNew && (
            <button
              onClick={() => setAddingNew(true)}
              className="text-yellow-600 hover:text-yellow-700 font-medium text-sm"
            >
              Add the first line item
            </button>
          )}
          {addingNew && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Performed By</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hours/Qty</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rate/Price</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Discount</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Billable</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-green-50">
                    {renderEditRow(newItem, handleNewChange, saveNewItem, () => {
                      setAddingNew(false);
                      setNewItem({ ...emptyLineItem });
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Invoice-level discount section */}
      <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <span className="text-sm font-medium text-gray-700">Invoice Discount:</span>
            {!editingDiscount ? (
              <>
                {invoice.discount_type ? (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-orange-100 text-orange-800">
                    {invoice.discount_type === 'percentage' ? (
                      <>{invoice.discount_value}% off</>
                    ) : (
                      <>{invoiceService.formatCurrency(invoice.discount_value)} off</>
                    )}
                    {invoice.discount_amount > 0 && (
                      <span className="ml-2 text-orange-600">(-{invoiceService.formatCurrency(invoice.discount_amount)})</span>
                    )}
                  </span>
                ) : (
                  <span className="text-sm text-gray-500">None</span>
                )}
              </>
            ) : (
              <div className="flex items-center space-x-2">
                <select
                  value={invoiceDiscountType || ''}
                  onChange={(e) => setInvoiceDiscountType(e.target.value || null)}
                  className="border border-gray-300 rounded px-2 py-1 text-sm"
                >
                  <option value="">No Discount</option>
                  <option value="fixed">$ Fixed</option>
                  <option value="percentage">% Percentage</option>
                </select>
                {invoiceDiscountType && (
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                      {invoiceDiscountType === 'percentage' ? '%' : '$'}
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={invoiceDiscountValue}
                      onChange={(e) => setInvoiceDiscountValue(e.target.value)}
                      className="w-24 border border-gray-300 rounded pl-6 pr-2 py-1 text-sm"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
          {isDraft && (
            <div>
              {!editingDiscount ? (
                <button
                  onClick={() => {
                    setInvoiceDiscountType(invoice.discount_type || null);
                    setInvoiceDiscountValue(invoice.discount_value || 0);
                    setEditingDiscount(true);
                  }}
                  className="text-yellow-600 hover:text-yellow-800 text-sm font-medium"
                >
                  Edit
                </button>
              ) : (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={saveInvoiceDiscount}
                    disabled={saving}
                    className="text-green-600 hover:text-green-800 text-sm font-medium"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => setEditingDiscount(false)}
                    className="text-gray-400 hover:text-gray-600 text-sm"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Totals summary */}
      <div className="px-6 py-4 border-t border-gray-200">
        <div className="flex justify-end">
          <div className="w-64 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Subtotal:</span>
              <span className="font-medium">{invoiceService.formatCurrency(invoice.subtotal)}</span>
            </div>
            {parseFloat(invoice.discount_amount || 0) > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-orange-600">Discount:</span>
                <span className="font-medium text-orange-600">-{invoiceService.formatCurrency(invoice.discount_amount)}</span>
              </div>
            )}
            {parseFloat(invoice.tax_amount) > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Tax ({invoice.tax_rate}%):</span>
                <span className="font-medium">{invoiceService.formatCurrency(invoice.tax_amount)}</span>
              </div>
            )}
            <div className="flex justify-between text-base font-bold border-t pt-2">
              <span>Total:</span>
              <span>{invoiceService.formatCurrency(invoice.total_amount)}</span>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
        confirmText={confirmDialog.confirmText}
      />
    </div>
  );
};

export default EditableLineItems;
